import { ArrowDownToLine, FolderOpen, GitCompareArrows, Trash2 } from '../components/icons';
import { useState } from 'react';
import { YearSelector } from '../components/YearSelector';
import { formatCurrency, formatDateTime } from '../domain/format';
import type { ScenarioSummary } from '../domain/types';
import { calculateScenario, summarizePeriod } from '../domain/scenarioEngine';
import { useScenario } from '../state/ScenarioContext';

const metrics: Array<{ key: keyof ScenarioSummary; label: string; inverse?: boolean }> = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'expenses', label: 'Modeled expenses', inverse: true },
  { key: 'profit', label: 'Modeled profit' },
  { key: 'endingCash', label: 'Ending cash' },
  { key: 'minimumCash', label: 'Minimum cash' },
  { key: 'arRecovery', label: 'AR recovered' },
];

export const ScenarioCompareView = () => {
  const { data, result, inputs, savedScenarios, loadScenario, deleteScenario } = useScenario();
  const [selectedYear, setSelectedYear] = useState(data.asOfMonth.slice(0, 4));
  const yearMonths = result.months.filter(({ month }) => month.startsWith(`${selectedYear}-`));
  const columns = [
    { id: 'plan', name: 'Original plan', summary: summarizePeriod(yearMonths, 'baseline'), current: false },
    { id: 'current', name: inputs.name || 'Working scenario', summary: summarizePeriod(yearMonths, 'scenario'), current: true },
    ...savedScenarios.map((scenario) => {
      const saved = calculateScenario(data, scenario.inputs).months.filter(({ month }) => month.startsWith(`${selectedYear}-`));
      return { id: scenario.id, name: scenario.name, summary: summarizePeriod(saved, 'scenario'), current: false };
    }),
  ];
  const maxProfit = Math.max(...columns.map((column) => Math.abs(column.summary.profit)), 1);

  return (
    <div className="page-stack">
      <section className="page-heading compact">
        <div><p className="eyebrow">Side-by-side decisions · 12 months</p><h2>Compare the outcome, not just the assumptions.</h2><p>Save scenario settings from the top bar, then compare one calendar year against the same versioned finance dataset.</p></div>
        <YearSelector months={data.months} value={selectedYear} onChange={setSelectedYear} />
      </section>
      <section className="panel comparison-chart">
        <div className="panel-heading"><div><p className="eyebrow">Modeled profit · {selectedYear}</p><h3>12-month scenario outcome</h3></div><GitCompareArrows size={20} /></div>
        <div className="comparison-bars">
          {columns.map((column) => (
            <div key={column.id}>
              <span>{column.name}</span>
              <div className="comparison-track"><span className={`${column.current ? 'current ' : ''}${column.summary.profit < 0 ? 'negative' : ''}`.trim()} style={{ width: `${Math.max(2, Math.abs(column.summary.profit) / maxProfit * 100)}%` }} /></div>
              <strong className={column.summary.profit < 0 ? 'negative-text' : ''}>{formatCurrency(column.summary.profit)}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="panel table-panel compare-table">
        <div className="responsive-table">
          <table>
            <thead><tr><th>Metric</th>{columns.map((column) => <th key={column.id} className={column.current ? 'current-column numeric' : 'numeric'}>{column.name}{column.current ? <small>Live edits</small> : null}</th>)}</tr></thead>
            <tbody>{metrics.map((metric) => <tr key={metric.key}><td><strong>{metric.label}</strong></td>{columns.map((column) => { const value = column.summary[metric.key]; return <td key={column.id} className={column.current ? 'current-column numeric' : 'numeric'}>{typeof value === 'number' ? formatCurrency(value) : String(value ?? '—')}</td>; })}</tr>)}</tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow">Saved scenarios</p><h3>Saved settings</h3></div><span>{savedScenarios.length} saved</span></div>
        {savedScenarios.length ? (
          <div className="saved-list">
            {savedScenarios.map((scenario) => (
              <article key={scenario.id}>
                <div className="saved-icon"><FolderOpen size={18} /></div>
                <div><strong>{scenario.name}</strong><span>Saved {formatDateTime(scenario.savedAt)}</span></div>
                <div><small>{selectedYear} profit</small><strong>{formatCurrency(columns.find(({ id }) => id === scenario.id)?.summary.profit ?? 0)}</strong></div>
                <div><small>Ending cash · Dec {selectedYear}</small><strong>{formatCurrency(columns.find(({ id }) => id === scenario.id)?.summary.endingCash ?? 0)}</strong></div>
                <button className="button compact" onClick={() => loadScenario(scenario)}><ArrowDownToLine size={14} />Load</button>
                <button className="icon-button danger" onClick={() => deleteScenario(scenario.id)} aria-label={`Delete ${scenario.name}`}><Trash2 size={16} /></button>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-state spacious"><GitCompareArrows size={28} /><strong>No saved scenarios yet</strong><span>Name the working scenario and select “Save scenario” to add it here.</span></div>
        )}
      </section>
    </div>
  );
};
